import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Typography,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  RestoreFromTrash as RestoreIcon,
  Info as InfoIcon,
  Archive as ArchiveIcon
} from '@mui/icons-material';
import categoryService from '../../services/categoryService';

const InactiveCategoriesList = ({ onActionSuccess }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [actionLoading, setActionLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', severity: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const flattenCategories = (categoryTree = []) => {
    const flat = [];

    const visit = (nodes = []) => {
      nodes.forEach((node) => {
        flat.push(node);
        if (Array.isArray(node.children) && node.children.length > 0) {
          visit(node.children);
        }
      });
    };

    visit(categoryTree);
    return flat;
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categoryService.getAllCategoriesAdmin();
      const flatCategories = flattenCategories(data);
      // Filtrer uniquement les catégories où isActive = false
      const inactiveCategories = flatCategories.filter(cat => cat.isActive === false);
      setCategories(inactiveCategories);
    } catch (err) {
      setError('Erreur lors du chargement des catégories');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      pending: 'warning',
      rejected: 'error',
      archived: 'default'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      active: 'Approuvée',
      pending: 'En attente',
      rejected: 'Rejetée',
      archived: 'Archivée'
    };
    return labels[status] || status;
  };

  const handleRestore = async (categoryId) => {
    try {
      setActionLoading(true);
      await categoryService.approveCategory(categoryId);
      setNotification({ message: 'Catégorie restaurée', severity: 'success' });
      fetchCategories();
      onActionSuccess?.();
    } catch (err) {
      setNotification({ message: 'Erreur lors de la restauration', severity: 'error' });
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleArchive = async (categoryId) => {
    try {
      setActionLoading(true);
      await categoryService.archiveCategory(categoryId);
      setNotification({ message: 'Catégorie archivée', severity: 'success' });
      fetchCategories();
      onActionSuccess?.();
    } catch (err) {
      setNotification({ message: 'Erreur lors de l\'archivage', severity: 'error' });
      console.error(err);
    } finally {
      setActionLoading(false);
    }
  };

  const inactiveStatus = statusFilter === 'all'
    ? categories
    : categories.filter(cat => cat.status === statusFilter);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {notification.message && (
        <Alert severity={notification.severity} sx={{ mb: 2 }}>
          {notification.message}
        </Alert>
      )}

      <FormControl sx={{ mb: 3, minWidth: 200 }}>
        <InputLabel>Filtrer par statut</InputLabel>
        <Select
          value={statusFilter}
          label="Filtrer par statut"
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <MenuItem value="all">Tous les statuts</MenuItem>
          <MenuItem value="active">Approuvées</MenuItem>
          <MenuItem value="pending">En attente</MenuItem>
          <MenuItem value="rejected">Rejetées</MenuItem>
          <MenuItem value="archived">Archivées</MenuItem>
        </Select>
      </FormControl>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Nom</strong></TableCell>
              <TableCell><strong>Slug</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              <TableCell><strong>Proposée par</strong></TableCell>
              <TableCell><strong>Date de création</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inactiveStatus.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="textSecondary">
                    Aucune catégorie non-active trouvée
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              inactiveStatus.map((category) => (
                <TableRow key={category.id} hover>
                  <TableCell>{category.name}</TableCell>
                  <TableCell><code>{category.slug}</code></TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(category.status)}
                      color={getStatusColor(category.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {category.submittedBy ? 'User' : 'Admin'}
                  </TableCell>
                  <TableCell>
                    {new Date(category.createdAt).toLocaleDateString('fr-FR')}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      {category.status === 'rejected' && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleRestore(category.id)}
                          disabled={actionLoading}
                          title="Restaurer et approuver"
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      )}
                      {category.status !== 'archived' && (
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleArchive(category.id)}
                          disabled={actionLoading}
                          title="Archiver"
                        >
                          <ArchiveIcon fontSize="small" />
                        </IconButton>
                      )}
                      {category.rejectionReason && (
                        <IconButton
                          size="small"
                          title={`Raison du rejet: ${category.rejectionReason}`}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default InactiveCategoriesList;
